import "./style.scss";
import { FC, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import LogoSvg from "@/src/svg/default/LogoSvg";
import BtnDefault from "../../../../BtnDefault/BtnDefault";
import { Editor } from "@tinymce/tinymce-react";
import { splitHtmlByBlocks, splitHtmlByBlocksWithFontConvert } from "../../../converterPtToRem";
import { TINYMCE_EDITOR_CONTENT_STYLE, TINYMCE_EDITOR_HEIGHT } from "../../../tinyMceEditorDisplayStyle";
import AutocompleteInput from "@/src/components/AutocompleteInput/AutocompleteInput";
import AOPDegreeSelect from "@/src/components/AdmissionsOfficePage/shared/AOPDegreeSelect/AOPDegreeSelect";
import AOPIdSourceRow, { AOP_ID_SOURCE_DEFAULT_VALUES } from "@/src/components/AdmissionsOfficePage/shared/AOPIdSourceRow";
import { useAutocomplete } from "@/src/hooks/useAutocomplete";
import { useAdmissionTaskPrefill, type AdmissionTaskPrefillPayload } from "@/src/hooks/useAdmissionTaskPrefill";
import { useAOPIdSourceFields } from "@/src/hooks/useAOPIdSourceFields";
import { syncCourseAndNationalityAutocompletes } from "@/src/constants/AdmissionOffice/admissionTaskPrefillMappings";
import { admissionOfficeRequests } from "@/src/api/admissionOfficeDocCreateRequests";
import { AOPSwitzerlandAdmissionData } from "@/src/types/admissionOffice";
import { Nationalities } from "@/src/constants/globalConsts";
import { admissionOfficeWhoseDoc } from "@/src/constants/AdmissionOffice/admissionsOfficeConsts";
import { SWITZERLAND_ADMISSION_SIGNATURE_LABELS } from "@/src/constants/AdmissionOffice/Switzerland/AOSwitzerlandEnConsts";
import { ADMISSION_LETTER_BASIC_REGISTRY } from "../../AdmissionLetterBasic/admissionLetterBasicRegistry";
import { AODSvgExport } from "@/src/svg/admissionOfficePage/AOPSvgExport";
import { useDispatch } from "react-redux";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import { useAdmissionGeneratedDocumentActions } from "@/src/hooks/useAdmissionGeneratedDocumentActions";
import AOPGeneratedDocumentToolbar from "@/src/components/AdmissionsOfficePage/AOPGeneratedDocumentToolbar";
import { joinSelectedCourse, splitSelectedCourse } from "@/src/utils/admissionOffice/splitSelectedCourse";

interface IAOPFormValues {
  whose_doc: string;
  currentDate: string;
  fullName: string;
  dateOfBirth: string;
  nationality: string;
  course: string;
  selectedCourse: string;
  degree?: string;
  passport_number: string;
  academicYear: string;
  mediumOfInstruction: string;
  duration: string;
  coursePrefix: string;
  signature: string;
  task_id?: string;
  student_id?: string;
  fieldEmail?: boolean;
  userEmail?: string;
}

interface AOPConfigProps {
  dataForGeneration: AOPSwitzerlandAdmissionData;
  setDataForGeneration: React.Dispatch<React.SetStateAction<AOPSwitzerlandAdmissionData>>;
  setBodyForPrev: React.Dispatch<React.SetStateAction<AOPSwitzerlandAdmissionData>>;
}

const today = new Date();
const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDate = (value: string) => {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  return `${day}.${month}.${year}`;
};

const { admissionCourses } = ADMISSION_LETTER_BASIC_REGISTRY.malta;

const AOPConfig: FC<AOPConfigProps> = ({ dataForGeneration, setDataForGeneration, setBodyForPrev }) => {
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm<IAOPFormValues>({
    defaultValues: {
      currentDate: formatDateForInput(today),
      signature: "Admission Letter",
      ...AOP_ID_SOURCE_DEFAULT_VALUES,
    },
  });

  const dispatch = useDispatch();
  const [bodyContent, setBodyContent] = useState("");
  const [isPreviewReady, setIsPreviewReady] = useState(false);
  const applyIdSourceLockRef = useRef<(payload: AdmissionTaskPrefillPayload) => void>(() => {});

  const generatedDoc = useAdmissionGeneratedDocumentActions({
    getValues,
    setValue: (name, value) => setValue(name as keyof IAOPFormValues, value as never),
    studentIdFormField: "student_id",
    documentType: "admission",
  });

  const fieldEmail = watch("fieldEmail");
  const userEmail = watch("userEmail");
  const course = watch("course");

  const signatureAuto = useAutocomplete(SWITZERLAND_ADMISSION_SIGNATURE_LABELS);
  const nationalitiesAuto = useAutocomplete(Nationalities);
  const whoseDocAuto = useAutocomplete(admissionOfficeWhoseDoc);
  const offeredCourseAuto = useAutocomplete(Object.keys(admissionCourses));

  const { applyTaskPrefill, lastPrefilledTaskIdRef } = useAdmissionTaskPrefill<IAOPFormValues>({
    setValue,
    country: "switzerland",
    documentSlug: "admission",
    onPrefill: (payload, mapping, formBaseline) => {
      generatedDoc.setTaskPrefillSnapshot(payload, mapping, formBaseline);
      syncCourseAndNationalityAutocompletes(payload, offeredCourseAuto, nationalitiesAuto);
      applyIdSourceLockRef.current(payload);
    },
  });

  const idSource = useAOPIdSourceFields<IAOPFormValues>({
    setValue,
    getValues,
    applyTaskPrefill,
    lastPrefilledTaskIdRef,
    fieldEmail: !!fieldEmail,
    userEmail,
  });
  applyIdSourceLockRef.current = idSource.applyIdSourceLockFromPrefill;

  useEffect(() => {
    if (!course) {
      setBodyContent("");
      return;
    }
    const courseSettings = admissionCourses[course];
    if (!courseSettings) {
      setBodyContent("");
      return;
    }
    const { degree, selectedCourse } = splitSelectedCourse(courseSettings.selected_course);
    setValue("degree", degree);
    setValue("selectedCourse", selectedCourse);
    setValue("mediumOfInstruction", courseSettings.medium_of_instruction);
    setValue("duration", courseSettings.duration);
    setBodyContent(courseSettings.tinyMCEContent);
  }, [course, setValue]);

  const generateNewDocument = async () => {
    generatedDoc.resetGeneratedState();
    dispatch(setIsPreloyder(true));
    try {
      const taskReady = await generatedDoc.ensureTaskReadyForDocumentGenerate();
      if (!taskReady) return;
      const response = await admissionOfficeRequests.createSwitzerlandAdmissionDoc(dataForGeneration);
      generatedDoc.onDocumentGenerated(response);
    } catch {
      alert("An unexpected error occurred - write to the developers");
    } finally {
      dispatch(setIsPreloyder(false));
    }
    setIsPreviewReady(false);
  };

  const handlePreview = () => {
    const watched = {
      whose_doc: watch("whose_doc"),
      currentDate: watch("currentDate"),
      fullName: watch("fullName"),
      dateOfBirth: watch("dateOfBirth"),
      nationality: watch("nationality"),
      selectedCourse: watch("selectedCourse"),
      degree: watch("degree"),
      passport_number: watch("passport_number"),
      academicYear: watch("academicYear"),
      mediumOfInstruction: watch("mediumOfInstruction"),
      duration: watch("duration"),
      coursePrefix: watch("coursePrefix"),
      signature: watch("signature"),
    };
    const courseValue = joinSelectedCourse(watched.degree, watched.selectedCourse);

    const payload: AOPSwitzerlandAdmissionData = {
      currentDate: watched.currentDate ? parseDate(watched.currentDate) : "",
      fullName: watched.fullName || "",
      nationality: watched.nationality || "",
      course: courseValue || "",
      userOffice: "01",
      whose_doc: watched.whose_doc || "",
      title: watched.signature || "Admission Letter",
      dateOfBirth: watched.dateOfBirth ? parseDate(watched.dateOfBirth) : "",
      passport_number: watched.passport_number || "",
      academicYear: watched.academicYear || "",
      duration: watched.duration || "",
      mediumOfInstruction: watched.mediumOfInstruction || "",
      coursePrefix: watched.coursePrefix || "",
      body: splitHtmlByBlocks(bodyContent),
    };

    setDataForGeneration((prev) => ({ ...prev, ...payload }));
    setBodyForPrev((prev) => ({
      ...prev,
      ...payload,
      body: splitHtmlByBlocksWithFontConvert(bodyContent),
    }));

    const ready =
      Boolean(watched.fullName?.trim()) &&
      Boolean(watched.nationality?.trim()) &&
      Boolean(courseValue) &&
      Boolean(watched.signature?.trim()) &&
      bodyContent.trim().length > 0;

    if (ready) {
      setIsPreviewReady(true);
      generatedDoc.resetGeneratedState();
    } else {
      setIsPreviewReady(false);
    }
  };

  const handleCopy = async (fieldName: keyof IAOPFormValues) => {
    const value = getValues(fieldName);
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
    } catch (err) {
      console.error("Ошибка копирования", err);
    }
  };

  return (
    <>
      <div className="admission-office-page-config">
        <div className="admission-office-page-config-logo">
          <div className="logo-svg">
            <LogoSvg />
          </div>
          <div className="logo-line"></div>
          <p>Platforma</p>
        </div>
        <p className="admission-office-page-config-title">ADMISSION/CONFIRMATION LETTER</p>
        <form className="admission-office-page-config-form">
          <AOPIdSourceRow
            control={control}
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            applyTaskPrefill={idSource.applyTaskPrefill}
            lastPrefilledTaskIdRef={lastPrefilledTaskIdRef}
            lastPrefilledStudentIdRef={idSource.lastPrefilledStudentIdRef}
            fieldEmail={!!fieldEmail}
            isTaskIdDisabled={idSource.isTaskIdDisabled}
            isStudentIdDisabled={idSource.isStudentIdDisabled}
            isFieldEmailDisabled={idSource.isFieldEmailDisabled}
            onClearTaskId={idSource.handleClearTaskId}
            onClearStudentId={idSource.handleClearStudentId}
            onClearUserEmail={idSource.handleClearUserEmail}
            onCheckUserEmail={() => void idSource.handleCheckUserEmail()}
            onCopy={idSource.handleCopyIdField}
          />
          <div className="form-group-fields">
            <div className="form-group-fields-container">
              <div className="form-group-fields-input whose_doc">
                <AutocompleteInput
                  placeholder="Whose"
                  value={whoseDocAuto.query}
                  onChange={whoseDocAuto.onChange}
                  onSelect={(v) => {
                    whoseDocAuto.onSelect(v);
                    setValue("whose_doc", v);
                  }}
                  isOpen={whoseDocAuto.isOpen}
                  options={whoseDocAuto.filtered}
                  error={!!errors.whose_doc}
                  inputProps={{
                    register: register("whose_doc"),
                    close: whoseDocAuto.close,
                    setOpen: whoseDocAuto.onClick,
                    onArrowClick: whoseDocAuto.onArrowClick,
                  }}
                  identifier="whose"
                />
              </div>
              <div className="form-group-fields-input coursePrefix">
                <input className="coursePrefix" id="coursePrefix" type="text" {...register("coursePrefix")} />
              </div>
            </div>
            <div className="form-group-fields-input currentDate">
              <div className="form-group-fields-input-label">
                <label htmlFor="currentDate">Date of Issue</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="currentDate" id="currentDate" type="date" {...register("currentDate")} />
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="full-name">
                  Full Name<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="full-mame" id="full-mame" type="text" {...register("fullName", { required: true })} />
              {errors.fullName && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <div className="form-group-fields-input-svg-line"></div>
                <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("fullName")}>
                  <AODSvgExport.FullNameCopySvg />
                </div>
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="dateOfBirth">Date of Birth</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="dateOfBirth" id="dateOfBirth" type="date" {...register("dateOfBirth")} />
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
            <AutocompleteInput
              label="Nationality"
              value={nationalitiesAuto.query}
              onChange={nationalitiesAuto.onChange}
              onSelect={(v) => {
                nationalitiesAuto.onSelect(v);
                setValue("nationality", v);
              }}
              isOpen={nationalitiesAuto.isOpen}
              options={nationalitiesAuto.filtered}
              error={!!errors.nationality}
              inputProps={{
                register: register("nationality", { required: true }),
                close: nationalitiesAuto.close,
                setOpen: nationalitiesAuto.onClick,
                onArrowClick: nationalitiesAuto.onArrowClick,
              }}
              identifier="nationality"
            />
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input academic-year">
              <div className="form-group-fields-input-label">
                <label htmlFor="academic-year">Academic Year</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="academic-year" id="academic-year" type="text" {...register("academicYear")} />
            </div>
            <div className="form-group-fields-input passport-number">
              <div className="form-group-fields-input-label">
                <label htmlFor="passport-number">Pasport Number</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="passport-number" id="passport-number" type="text" {...register("passport_number")} />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input course">
              <AutocompleteInput
                label="Select the offered course"
                value={offeredCourseAuto.query}
                onChange={offeredCourseAuto.onChange}
                onSelect={(v) => {
                  offeredCourseAuto.onSelect(v);
                  setValue("course", v);
                }}
                isOpen={offeredCourseAuto.isOpen}
                options={offeredCourseAuto.filtered}
                error={!!errors.course}
                clearButton
                inputProps={{
                  register: register("course"),
                  close: offeredCourseAuto.close,
                  setOpen: offeredCourseAuto.onClick,
                  onArrowClick: offeredCourseAuto.onArrowClick,
                  setValue,
                  clearErrors,
                  name: "course",
                }}
                identifier="offered-course-auto"
              />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="degree">Degree</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <AOPDegreeSelect control={control} />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="selected-сourse">Selected course</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="selected-сourse" id="selected-сourse" type="text" {...register("selectedCourse")} />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="medium-of-instruction">Medium of Instruction</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input
                className="medium-of-instruction"
                id="medium-of-instruction"
                type="text"
                {...register("mediumOfInstruction")}
              />
            </div>
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="duration">Duration</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="duration" id="duration" type="text" {...register("duration")} />
            </div>
          </div>
          <label>Admission body</label>
          <Editor
            value={bodyContent}
            init={{
              height: TINYMCE_EDITOR_HEIGHT,
              menubar: false,
              plugins: ["lists", "link", "code", "table"],
              toolbar:
                "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
              branding: false,
              toolbar_mode: "wrap",
              licenseKey: "gpl",
              license_key: "gpl" as any,
              content_style: TINYMCE_EDITOR_CONTENT_STYLE,
            }}
            onEditorChange={setBodyContent}
            tinymceScriptSrc="/tinymce/tinymce.min.js"
          />
          <AOPGeneratedDocumentToolbar
            isGenerated={generatedDoc.isGenerated}
            generatedPdfUrl={generatedDoc.generatedPdfUrl}
            taskAttachedPdfUrl={generatedDoc.taskAttachedPdfUrl}
            previewPdfUrl={generatedDoc.previewPdfUrl}
            canOpenPdfPreview={generatedDoc.canOpenPdfPreview}
            canOpenTaskDocumentEye={generatedDoc.canOpenTaskDocumentEye}
            isGeneratedPdfPreviewOpen={generatedDoc.isGeneratedPdfPreviewOpen}
            setIsGeneratedPdfPreviewOpen={generatedDoc.setIsGeneratedPdfPreviewOpen}
            generatedPreviewTitle={generatedDoc.generatedPreviewTitle}
            onDownload={generatedDoc.handleDownloadGeneratedPdf}
            onApprove={generatedDoc.handlePreviewApprove}
            onReject={generatedDoc.handlePreviewReject}
            isTaskDocumentCheckReview={generatedDoc.isTaskDocumentCheckReview}
            previewButton={
              <BtnDefault onClick={handlePreview} reverse={true}>
                <p>Preview doc</p>
                <AODSvgExport.PreviewSvg />
              </BtnDefault>
            }
            generateButton={
              <BtnDefault onClick={handleSubmit(generateNewDocument)} disabled={!isPreviewReady}>
                <p>Generate doc</p>
                <AODSvgExport.GenerateSvg />
              </BtnDefault>
            }
          />
          <div className="form-group-fields form-group-fields-signature-row">
            <div className="form-group-fields-input signature">
              <AutocompleteInput
                label="Signature"
                value={signatureAuto.query}
                defaultValue="Admission Letter"
                onChange={signatureAuto.onChange}
                onSelect={(v) => {
                  signatureAuto.onSelect(v);
                  setValue("signature", v);
                }}
                isOpen={signatureAuto.isOpen}
                options={signatureAuto.filtered}
                error={!!errors.signature}
                clearButton
                inputProps={{
                  register: register("signature", { required: true }),
                  close: signatureAuto.close,
                  setOpen: signatureAuto.onClick,
                  onArrowClick: signatureAuto.onArrowClick,
                  setValue,
                  clearErrors,
                  name: "signature",
                }}
                identifier="signature"
              />
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default AOPConfig;
